import { ArrowUpRight, Target } from "lucide-react";
import { SECTION_LABEL } from "@/lib/site";
import { track } from "@/lib/track";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { TiltCard } from "@/components/TiltCard";

const PROJECTS = [
    {
        name: "NomiConnekt",
        url: "https://nomiconnekt.in/",
        img: "/assets/nomiconnekt-full.jpg",
        alt: "Live screenshot of NomiConnekt — sign-in screen of the property management product for hotels and hostels",
        desc: "Property management software for hotel and hostel operations.",
        outcome:
            "Built as one operations dashboard, so bookings, guests and room status are managed from a single login.",
        tags: ["Web App", "Operations", "Hospitality"],
        cta: "View Live Product",
        id: "nomiconnekt",
        domain: "nomiconnekt.in",
    },
    {
        name: "Spice Route Expeditions",
        url: "https://www.spiceroutexpeditions.com/",
        img: "/assets/spice-route-expeditions-full.jpg",
        alt: "Live screenshot of the Spice Route Expeditions homepage — Himalayan travel experiences with trip planning",
        desc: "A travel business offering trip planning and customer-facing travel experiences.",
        outcome:
            "Trip enquiries arrive through a structured journey form, so every lead comes with destination, dates and traveller count attached.",
        tags: ["Website", "Travel", "Enquiries"],
        cta: "Visit Website",
        id: "spice-route",
        domain: "spiceroutexpeditions.com",
    },
    {
        name: "GoNomikai",
        url: "https://gonomikai.com/",
        img: "/assets/gonomikai-full.jpg",
        alt: "Live screenshot of the GoNomikai homepage — a boutique social stay in Gangtok with stay booking",
        desc: "A hospitality brand with a digital experience for discovering stays and bookings.",
        outcome:
            "Stay discovery and booking sit in one flow, with live availability shown before a guest ever has to call.",
        tags: ["Website", "Bookings", "Brand"],
        cta: "Explore Live Website",
        id: "gonomikai",
        domain: "gonomikai.com",
    },
];

export default function Work() {
    return (
        <section id="work" className="scroll-mt-20 bg-white" data-testid="work-section">
            <div className="mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
                <Reveal>
                    <p className={SECTION_LABEL}>Our Work</p>
                    <h2 className="mt-4 max-w-3xl font-heading text-3xl font-medium tracking-tight text-[#1C1917] md:text-4xl lg:text-[3.25rem] lg:leading-[1.1]">
                        Selected work.{" "}
                        <span className="font-serif italic text-[#CF4242]">Real products.</span>
                    </h2>
                    <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#57504A] md:text-lg">
                        Hover any project to scroll through the live page — then open it and judge
                        the real thing for yourself.
                    </p>
                </Reveal>

                <Stagger className="mt-16 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
                    {PROJECTS.map((p) => (
                        <StaggerItem key={p.id} className="h-full">
                            <TiltCard
                                data-testid={`work-card-${p.id}`}
                                className="group flex h-full flex-col overflow-hidden rounded-[1.25rem] border border-[#EDE3DA] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-shadow duration-300 hover:shadow-[0_32px_60px_-20px_rgb(28,25,23,0.3)]"
                            >
                                <div className="relative overflow-hidden border-b border-[#EDE3DA] bg-[#F7F0EA]">
                                    <div
                                        className="flex items-center gap-1.5 border-b border-[#EDE3DA] bg-white px-4 py-2.5"
                                        aria-hidden="true"
                                    >
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#E7D2C8] transition-colors duration-300 group-hover:bg-[#CF4242]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#EBDCCF] transition-colors duration-300 group-hover:bg-[#E8764A]" />
                                        <span className="h-2.5 w-2.5 rounded-full bg-[#F0E4D7]" />
                                        <span className="ml-2 rounded-full bg-[#F7F0EA] px-3 py-1 text-[11px] font-medium text-[#A89E95]">
                                            {p.domain}
                                        </span>
                                    </div>
                                    <div className="relative aspect-[8/5] w-full overflow-hidden">
                                        <img
                                            src={p.img}
                                            alt={p.alt}
                                            width={1440}
                                            height={900}
                                            loading="lazy"
                                            className="site-scroll absolute inset-0 h-full w-full object-cover"
                                            data-testid={`work-card-${p.id}-image`}
                                        />
                                        <span className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-[#1C1917]/80 px-3 py-1 text-[11px] font-semibold text-white opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
                                            Live preview
                                        </span>
                                    </div>
                                </div>

                                <div className="flex flex-1 flex-col p-7">
                                    <h3 className="font-heading text-xl font-semibold text-[#1C1917]">
                                        {p.name}
                                    </h3>
                                    <p className="mt-2 text-sm leading-relaxed text-[#57504A]">
                                        {p.desc}
                                    </p>
                                    <div
                                        className="mt-4 rounded-xl border border-[#F0E4D7] bg-[#FDF7F2] p-4"
                                        data-testid={`work-card-${p.id}-outcome`}
                                    >
                                        <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#CF4242]">
                                            <Target size={12} aria-hidden="true" /> Outcome
                                        </p>
                                        <p className="mt-1.5 text-sm leading-relaxed text-[#1C1917]">
                                            {p.outcome}
                                        </p>
                                    </div>
                                    <div className="mt-4 flex flex-1 flex-wrap items-start gap-2">
                                        {p.tags.map((t) => (
                                            <span
                                                key={t}
                                                className="rounded-full border border-[#EDE3DA] bg-[#FDF7F2] px-2.5 py-1 text-[11px] font-semibold text-[#8A807A]"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                    </div>
                                    <a
                                        href={p.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`work-card-${p.id}-link`}
                                        onClick={() => track("project_click", { project: p.id })}
                                        className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#CF4242] transition-colors duration-200 hover:text-[#A92F31]"
                                    >
                                        {p.cta}
                                        <ArrowUpRight
                                            size={16}
                                            className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                                            aria-hidden="true"
                                        />
                                    </a>
                                </div>
                            </TiltCard>
                        </StaggerItem>
                    ))}
                </Stagger>
            </div>
        </section>
    );
}
